import { useAdminStore } from "@/stores/admin";
import * as Sentry from "@sentry/vue";
import { defineStore } from "pinia";
import Typesense, { Client } from 'typesense';
import { computed, ref } from "vue";


export interface TypesenseJobSheetResult {
  id: string;
  jobNumber: string;
  customerName: string;
  customerId: string;
  address: string;
  instructions: string;
  docketNumbers?: string[];
  operatorName: string;
  operatorId: string;
  status: string;
  jobId: string;
  jobDate: number;
  isInvoiced: boolean;
  deleted: boolean;
  createdAt: number;
  updatedAt?: number;
  highlightedJobNumber?: string;
  highlightedCustomerName?: string;
  highlightedAddress?: string;
  highlightedInstructions?: string;
  highlightedReferenceNumber?: string;
  stops: string;
}

export interface TypesenseJobResult {
  id: string;
  jobNumber: string;
  referenceNumber?: string;
  customerName?: string;
  customerId: string;
  address: string;
  createdAt: number;
  updatedAt?: number;
}

// export const jobSheetSchema = (envId: string): CollectionCreateSchema => ({
//   name: `${COLLECTION_PREFIX}${envId}_jobsheets`,
//   fields: [
//     { name: "jobNumber", type: "string", facet: false },
//     { name: "customerName", type: "string", facet: false },
//     { name: "customerId", type: "string", facet: true },
//     { name: "address", type: "string", facet: false },
//     { name: "instructions", type: "string", facet: false },
//     { name: "docketNumbers", type: "string[]", facet: false, optional: true },
//     { name: "operatorName", type: "string", facet: false },
//     { name: "operatorId", type: "string", facet: true },
//     { name: "status", type: "string", facet: true },
//     { name: "jobId", type: "string", facet: true },
//     { name: "jobDate", type: "int64", facet: false },
//     { name: "isInvoiced", type: "bool", facet: true },
//     { name: "deleted", type: "bool", facet: true },
//     { name: "createdAt", type: "int64", facet: true },
//     { name: "updatedAt", type: "int64", facet: false, optional: true },
//   ],
//   default_sorting_field: "jobDate",
// });


// export const jobSchema = (envId: string): CollectionCreateSchema => ({
//   name: `${COLLECTION_PREFIX}${envId}_jobs`,
//   fields: [
//     { name: "jobNumber", type: "string", facet: false },
//     { name: "customerName", type: "string", facet: false },
//     { name: "customerId", type: "string", facet: true },
//     { name: "address", type: "string", facet: false },
//     { name: "createdAt", type: "int64", facet: true },
//     { name: "updatedAt", type: "int64", facet: false, optional: true },
//   ],
//   default_sorting_field: "createdAt", 
// });

export const useTypesenseStore = defineStore("typesense", () => {
  const typesenseJobsClient = ref<Client | null>(null);
  const typesenseJobSheetClient = ref<Client | null>(null);

  const adminStore = useAdminStore();

  const enviroment = computed(() => adminStore.environment);

  const typesenseJobsKey = computed(() => enviroment.value?.typesenseJobsKey?.value ?? "");
  const typesenseJobSheetKey = computed(() => enviroment.value?.typesenseJobSheetsKey?.value ?? "");

  const isInitialized = ref(false);

  const initTypesenseClients = async () => {

    if (!typesenseJobsClient.value && typesenseJobsKey.value) {
      try {
        typesenseJobsClient.value = new Typesense.Client({
          nodes: [
            {
              host: 'v7kp3exl2ft41iaqp-1.a1.typesense.net',
              protocol: 'https',
              port: 443,
            },
          ],
          apiKey: typesenseJobsKey.value,
          connectionTimeoutSeconds: 5,
        });
      } catch (error) {
        Sentry.captureException(error);
      }
    } 

    if (!typesenseJobSheetClient.value && typesenseJobSheetKey.value) {
      try {
        typesenseJobSheetClient.value = new Typesense.Client({
          nodes: [
            {
              host: 'v7kp3exl2ft41iaqp-1.a1.typesense.net',
              protocol: 'https',
              port: 443,
            },
          ],
          apiKey: typesenseJobSheetKey.value,
          connectionTimeoutSeconds: 5,
        });
        isInitialized.value = true;
      } catch (error) {
        Sentry.captureException(error);
      }
    } 
  }

  const searchJobs = async (query: {
    search?: string;
    page?: number;
    perPage?: number;
  }) => {
    const collectionName = `intransvoice_${enviroment.value?.id}_jobs`;


    if (!typesenseJobsClient.value) {
      await initTypesenseClients();
    }

    if (!typesenseJobsClient.value) {
      return { results: [], found: 0 };
    }
  
    try {
      const searchParameters = {
        q: query.search || "*",
        query_by: "jobNumber,customerName,address,referenceNumber",
        page: query.page || 1,
        per_page: query.perPage || 20,
        highlight_full_fields: "jobNumber,customerName,address,referenceNumber",
      };
  
      const searchResults = await typesenseJobsClient.value
        .collections(collectionName)
        .documents()
        .search(searchParameters);
  
      return {
        results: searchResults?.hits?.map(hit => ({
          ...hit.document as TypesenseJobResult,
          customer: { Name: (hit.document as any).customerName },
          highlightedJobNumber: (hit.highlight as any)?.jobNumber?.snippet,
          highlightedCustomerName: (hit.highlight as any)?.customerName?.snippet,
          highlightedAddress: (hit.highlight as any)?.address?.snippet,
          highlightedReferenceNumber: (hit.highlight as any)?.referenceNumber?.snippet,
        })),
        found: searchResults.found,
      };
    } catch (error) {
      Sentry.captureException(error);
      return { results: [], found: 0 };
    }
  };

  const searchJobSheets = async (query: {
    search?: string;
    page?: number;
    perPage?: number;
    filter?: string;
  }) => {

    const collectionName = `intransvoice_${enviroment.value?.id}_jobsheets`;


    if (!typesenseJobSheetClient.value) {
      await initTypesenseClients();
    }

    if (!typesenseJobSheetClient.value) {
      return { results: [], found: 0 };
    }

    try {
      const searchParameters: any = {
        q: query.search || "*",
        query_by:
          "jobNumber,customerName,address,instructions,referenceNumber,docketNumbers",
        page: query.page || 1,
        per_page: query.perPage || 20,
        highlight_full_fields: "jobNumber,customerName,address,instructions,referenceNumber",
        sort_by: "jobDate:desc",
      };

      if (query.filter) {
        searchParameters.filter_by = query.filter;
      }

      const searchResults = await typesenseJobSheetClient.value.collections(collectionName).documents().search(searchParameters);


      return {
        results: searchResults?.hits?.map(hit => ({
          ...hit.document as TypesenseJobSheetResult,
          highlightedJobNumber: (hit.highlight as any)?.jobNumber?.snippet,
          highlightedCustomerName: (hit.highlight as any)?.customerName?.snippet,
          highlightedAddress: (hit.highlight as any)?.address?.snippet,
          highlightedInstructions: (hit.highlight as any)?.instructions?.snippet,
          highlightedReferenceNumber: (hit.highlight as any)?.referenceNumber?.snippet,
        })),
        found: searchResults.found,
      };
    } catch (error) {
      Sentry.captureException(error);
      return { results: [], found: 0 };
    }
  };

  return {
    searchJobs,
    initTypesenseClients,
    searchJobSheets,
    getJobsClient: () => typesenseJobsClient.value,
    getJobSheetsClient: () => typesenseJobSheetClient.value,
    isInitialized,
  }


});
